// Global drag-and-drop overlay. Mounts window-level drag listeners and shows
// a full-screen drop target whenever the user drags files over the page. On
// drop, dispatches a `niu:files-dropped` CustomEvent with the File[] — the
// composer listens for this and adds the files to its pending list, reusing
// the same code path as the in-bar file picker.

import { useEffect, useState } from 'react';
import { FilePlusIcon } from './Icons';

export const FILES_DROPPED_EVENT = 'niu:files-dropped';

export interface FilesDroppedDetail {
  files: File[];
}

function dragHasFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  for (let i = 0; i < types.length; i++) {
    if (types[i] === 'Files') return true;
  }
  return false;
}

export function DragDropOverlay() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    // dragenter/dragleave fire for every child element, so we keep a counter
    // and only deactivate when it returns to zero.
    let counter = 0;

    function onDragEnter(e: DragEvent) {
      if (!dragHasFiles(e)) return;
      counter++;
      setActive(true);
    }
    function onDragLeave(e: DragEvent) {
      if (!dragHasFiles(e)) return;
      counter = Math.max(0, counter - 1);
      if (counter === 0) setActive(false);
    }
    function onDragOver(e: DragEvent) {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    }
    function onDrop(e: DragEvent) {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      counter = 0;
      setActive(false);
      const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      if (files.length === 0) return;
      window.dispatchEvent(
        new CustomEvent<FilesDroppedDetail>(FILES_DROPPED_EVENT, { detail: { files } }),
      );
    }

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  if (!active) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-background/75 backdrop-blur-sm animate-fade-in">
      <div className="flex flex-col items-center gap-4 px-6 text-center text-text-primary">
        <FilePlusIcon />
        <div className="text-base font-medium sm:text-lg">Drop files to add them to the chat</div>
      </div>
    </div>
  );
}
